import type {
  PasswordValidation,
  ValidationResult,
} from '@/lib/types/validation';

export interface OptionItem {
  value: string;
  label: string;
}

export interface CheckboxOptions {
  [key: string]: boolean;
}

export function scrollToTop(): void {
  window.scrollTo({
    behavior: 'smooth',
    top: 0,
  });
}

export function validatePassword(password: string): PasswordValidation {
  return {
    atLeastOneLowercaseLetter: /[a-z]/.test(password),
    atLeastOneNumberOrSymbol: /[0-9!@#$%^&*(),.?":{}|<>]/.test(password),
    atLeastOneUppercaseLetter: /[A-Z]/.test(password),
    lengthGreaterThanEight: password.length > 8,
  };
}

export function parseNestedJson(text: string | object): unknown {
  try {
    const outerJson = typeof text === 'string' ? JSON.parse(text) : text;

    // Check if the message property exists and is a string
    if (outerJson.message && typeof outerJson.message === 'string') {
      try {
        const innerJson = JSON.parse(outerJson.message);

        // Return a new object with the parsed message
        return {
          ...outerJson,
          message: innerJson,
        };
      } catch (innerError) {
        console.error('Failed to parse the inner JSON message:', innerError);
        // Return the original object if inner parsing fails
        return outerJson;
      }
    }

    // Return the original object if it doesn't have a message property
    return outerJson;
  } catch (outerError) {
    console.error('Failed to parse the outer JSON:', outerError);
    throw new Error('Invalid JSON format');
  }
}

export function getTrueKeys(obj: { [key: string]: unknown }): string[] {
  return Object.keys(obj).filter(key => obj[key]);
}

export function validateCheckboxScreeningQuestions(
  checkboxOptions: CheckboxOptions
): ValidationResult {
  // Check if any option is selected
  const hasSelection = Object.values(checkboxOptions).some(
    value => value === true
  );

  if (!hasSelection) {
    return {
      error: 'Please select at least one option',
      isValid: false,
    };
  }

  // Check if "None of the above" or "Prefer not to answer" is selected along with other options
  const exclusiveOptions = [
    'noneOfTheAbove',
    'preferNotToAnswer',
    'notSure',
    'hasNeverHadSex',
  ];

  const hasExclusiveOption = exclusiveOptions.some(
    option => checkboxOptions[option] === true
  );
  const hasRegularOption = Object.entries(checkboxOptions)
    .filter(([key]) => !exclusiveOptions.includes(key))
    .some(([, value]) => value === true);

  if (hasExclusiveOption && hasRegularOption) {
    // Determine which exclusive option was selected for a more specific error message
    const selectedExclusiveOption = exclusiveOptions.find(
      option => checkboxOptions[option] === true
    );

    let errorMsg = '';
    if (selectedExclusiveOption === 'noneOfTheAbove') {
      errorMsg =
        "If 'None of the above' is selected, no other options should be selected.";
    } else if (selectedExclusiveOption === 'preferNotToAnswer') {
      errorMsg =
        "If 'Prefer not to answer' is selected, no other options should be selected.";
    } else if (selectedExclusiveOption === 'notSure') {
      errorMsg =
        "If 'Not sure' is selected, no other options should be selected.";
    } else if (selectedExclusiveOption === 'hasNeverHadSex') {
      errorMsg =
        "If 'I have never had sex' is selected, no other options should be selected.";
    }

    return {
      error: errorMsg,
      isValid: false,
    };
  }

  // Also need to check if multiple exclusive options are selected
  const selectedExclusiveOptions = exclusiveOptions.filter(
    option => checkboxOptions[option] === true
  );
  if (selectedExclusiveOptions.length > 1) {
    return {
      error:
        "You cannot select 'None of the above', 'Not sure', and 'Prefer not to answer' at the same time.",
      isValid: false,
    };
  }

  // If we get here, validation passed
  return {
    error: null,
    isValid: true,
  };
}

// Function to convert an array of values to an array of labels
// Using this for all the checkbox answers
export function valuesToLabels(
  values: string[],
  optionsArray: OptionItem[]
): string[] {
  return values
    .map(value => {
      const matchingOption = optionsArray.find(
        option => option.value === value
      );
      return matchingOption ? matchingOption.label : null;
    })
    .filter(label => label !== null);
}

export function validateCode(code: string): ValidationResult {
  if (!code) return { error: 'activation code is required', isValid: false };

  const trimmedCode = code.trim().toUpperCase();

  // Check if it's a TST code first (TST-XXX-XXX format, 11 characters)
  if (trimmedCode.startsWith('TST')) {
    if (trimmedCode.length !== 11) {
      return {
        error: 'TST codes must be 11 characters (TST-XXX-XXX)',
        isValid: false,
      };
    }

    const tstPattern = /^TST-[A-Z0-9]{3}-[A-Z0-9]{3}$/;
    if (!tstPattern.test(trimmedCode)) {
      return { error: 'invalid code format', isValid: false };
    }

    return { error: null, isValid: true };
  }

  // Check regular codes (7 characters)
  if (trimmedCode.length !== 7) {
    return { error: 'activation code must be 7 characters', isValid: false };
  }

  // Regular expression breakdown:
  // ^         - Start of string
  // [TVDS]    - First character must be T, V, D, or S
  // [A-Z0-9]{2} - Followed by exactly 2 alphanumeric characters
  // -         - Followed by a hyphen
  // [A-Z0-9]{3} - Followed by exactly 3 alphanumeric characters
  // $         - End of string
  const codePattern = /^[TVDS][A-Z0-9]{2}-[A-Z0-9]{3}$/;

  if (!codePattern.test(trimmedCode)) {
    return { error: 'invalid code format', isValid: false };
  }

  return { error: null, isValid: true };
}
